class CollectionFilters {
  constructor() {
    this.filterForm = document.querySelector('.collection-filters');
    this.sortForm = document.querySelector('#SortByForm');
    this.productGrid = document.querySelector('#product-grid');
    this.resultsInfo = document.querySelector('.collection-results-info');
    this.pagination = document.querySelector('.pagination');
    this.clearFiltersBtn = document.querySelector('.clear-filters-btn');
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateActiveFilters();
    this.handlePaginationVisibility();
  }

  bindEvents() {
    // Handle filter checkbox changes
    if (this.filterForm) {
      this.filterForm.addEventListener('change', (e) => {
        if (e.target.type === 'checkbox') {
          this.handleFilterChange(e.target);
        }
      });
    }

    // Handle sort form changes (already has onchange="this.form.submit()" but we'll enhance it)
    if (this.sortForm) {
      const sortSelect = this.sortForm.querySelector('#SortBy');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          this.handleSortChange();
        });
      }
    }

    // Handle clear all filters
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearAllFilters();
      });
    }

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.collection-filters')) {
        const openDetails = this.filterForm?.querySelectorAll('details[open]');
        openDetails?.forEach(details => {
          details.removeAttribute('open');
          const summary = details.querySelector('summary');
          summary?.setAttribute('aria-expanded', 'false');
        });
      }
    });

    window.addEventListener('popstate', () => {
      this.loadCollection(window.location.search, false);
    });
  }

  handleFilterChange(checkbox) {
    // Show loading state
    this.showLoading();

    // Get current URL parameters
    const params = new URLSearchParams(window.location.search);
    const filterName = checkbox.name;
    const filterValue = checkbox.value;

    if (checkbox.checked) {
      // Add filter value
      const existingValues = params.getAll(filterName);
      if (!existingValues.includes(filterValue)) {
        params.append(filterName, filterValue);
      }
    } else {
      // Remove filter value
      const existingValues = params.getAll(filterName);
      params.delete(filterName);
      
      // Re-add all values except the unchecked one
      existingValues.forEach(value => {
        if (value !== filterValue) {
          params.append(filterName, value);
        }
      });
    }

    // Reset to page 1 when filters change
    params.delete('page');

    // Update URL and load new results
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    this.loadCollection(params.toString(), true);
  }

  handleSortChange() {
    this.showLoading();
    
    const sortValue = this.sortForm.querySelector('#SortBy').value;
    const params = new URLSearchParams(window.location.search);
    
    if (sortValue) {
      params.set('sort_by', sortValue);
    } else {
      params.delete('sort_by');
    }

    // Reset to page 1 when sorting changes
    params.delete('page');

    this.loadCollection(params.toString(), true);
  }

  clearAllFilters() {
    this.showLoading();

    // Keep only sort_by parameter if it exists
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get('sort_by');
    
    const newParams = new URLSearchParams();
    if (sortBy) {
      newParams.set('sort_by', sortBy);
    }

    this.loadCollection(newParams.toString(), true);
  }

  async loadCollection(queryString, updateHistory = true) {
    try {
      const url = `${window.location.pathname}?${queryString}`;
      
      // Fetch the new page
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(html, 'text/html');

      // Update the product grid
      const newProductGrid = newDocument.querySelector('#product-grid');
      if (newProductGrid && this.productGrid) {
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }

      // Update results info
      const newResultsInfo = newDocument.querySelector('.collection-results-info');
      if (newResultsInfo && this.resultsInfo) {
        this.resultsInfo.innerHTML = newResultsInfo.innerHTML;
      }

      // Update pagination
      const newPagination = newDocument.querySelector('.pagination');
      if (newPagination) {
        // If server returns pagination, update it
        if (this.pagination) {
          this.pagination.innerHTML = newPagination.innerHTML;
          this.pagination.classList.add('has-pages');
        } else {
          // Create pagination element if it doesn't exist
          const paginationNav = document.createElement('nav');
          paginationNav.className = 'pagination has-pages';
          paginationNav.setAttribute('role', 'navigation');
          paginationNav.setAttribute('aria-label', 'Pagination Navigation');
          paginationNav.innerHTML = newPagination.innerHTML;
          
          // Insert after product grid
          this.productGrid.parentNode.insertBefore(paginationNav, this.productGrid.nextSibling);
          this.pagination = paginationNav;
        }
      } else {
        // No pagination in response, hide existing pagination
        if (this.pagination) {
          this.pagination.classList.remove('has-pages');
          this.pagination.innerHTML = '';
        }
      }

      // Handle pagination visibility after update
      this.handlePaginationVisibility();

      // Update filters to reflect new state
      this.updateFiltersFromResponse(newDocument);

      // Update URL if needed
      if (updateHistory) {
        window.history.pushState(null, '', url);
      }

      // Scroll to top of results
      this.productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Trigger custom event for other scripts that might need to know about the update
      window.dispatchEvent(new CustomEvent('collectionUpdated', {
        detail: { url, queryString }
      }));

    } catch (error) {
      console.error('Error loading collection:', error);
      this.showError();
    } finally {
      this.hideLoading();
    }
  }

  updateFiltersFromResponse(newDocument) {
    const newFilters = newDocument.querySelector('.collection-filters');
    if (newFilters && this.filterForm) {
      const newCheckboxes = newFilters.querySelectorAll('input[type="checkbox"]');
      const currentCheckboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');

      newCheckboxes.forEach(newCheckbox => {
        const currentCheckbox = this.filterForm.querySelector(
          `input[name="${newCheckbox.name}"][value="${newCheckbox.value}"]`
        );
        
        if (currentCheckbox) {
          currentCheckbox.checked = newCheckbox.checked;
          currentCheckbox.disabled = newCheckbox.disabled;
          
          // Update count text
          const countElement = currentCheckbox.closest('li').querySelector('.filter-count-text');
          const newCountElement = newCheckbox.closest('li').querySelector('.filter-count-text');
          if (countElement && newCountElement) {
            countElement.textContent = newCountElement.textContent;
          }
        }
      });

      // Update clear filters button - FIXED LOGIC
      const newClearContainer = newDocument.querySelector('.clear-filters-container');
      const currentClearContainer = document.querySelector('.clear-filters-container');
      
      if (newClearContainer) {
        if (currentClearContainer) {
          currentClearContainer.innerHTML = newClearContainer.innerHTML;
        } else {
          const clearContainer = document.createElement('div');
          clearContainer.className = 'clear-filters-container';
          clearContainer.innerHTML = newClearContainer.innerHTML;
          this.filterForm.appendChild(clearContainer);
        }
        
        this.clearFiltersBtn = document.querySelector('.clear-filters-btn');
        if (this.clearFiltersBtn) {
          this.clearFiltersBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.clearAllFilters();
          });
        }
      } else {
        if (currentClearContainer) {
          currentClearContainer.remove();
        }
        this.clearFiltersBtn = null;
      }
    }

    this.updateActiveFilters();
  }

  updateActiveFilters() {
    const filterDetails = this.filterForm?.querySelectorAll('details');
    
    filterDetails?.forEach(details => {
      const checkboxes = details.querySelectorAll('input[type="checkbox"]:checked');
      const summary = details.querySelector('summary');
      
      if (checkboxes.length > 0) {
        details.setAttribute('open', '');
        summary?.setAttribute('aria-expanded', 'true');
      } else {
        summary?.setAttribute('aria-expanded', 'false');
      }
    });
  }

  handlePaginationVisibility() {
    if (this.pagination) {
      const paginationLinks = this.pagination.querySelectorAll('.pagination__link');
      const hasMultiplePages = paginationLinks.length > 1;
      
      if (hasMultiplePages) {
        this.pagination.classList.add('has-pages');
      } else {
        this.pagination.classList.remove('has-pages');
      }
    }
  }

  showLoading() {
    if (this.productGrid) {
      this.productGrid.classList.add('loading');
      this.productGrid.style.opacity = '0.6';
      this.productGrid.style.pointerEvents = 'none';
    }

    const formElements = document.querySelectorAll('.collection-filters input, #SortBy');
    formElements.forEach(element => {
      element.disabled = true;
    });
  }

  hideLoading() {
    if (this.productGrid) {
      this.productGrid.classList.remove('loading');
      this.productGrid.style.opacity = '';
      this.productGrid.style.pointerEvents = '';
    }

    const formElements = document.querySelectorAll('.collection-filters input, #SortBy');
    formElements.forEach(element => {
      element.disabled = false;
    });

    const checkboxes = document.querySelectorAll('.collection-filters input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      const countText = checkbox.closest('li')?.querySelector('.filter-count-text')?.textContent;
      if (countText && countText.includes('(0)') && !checkbox.checked) {
        checkbox.disabled = true;
      }
    });
  }

  showError() {
    if (this.resultsInfo) {
      this.resultsInfo.innerHTML = '<p class="error-message" style="color: #dc3545;">Sorry, there was an error loading the products. Please try again.</p>';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CollectionFilters();
});

document.addEventListener('click', (e) => {
  if (e.target.closest('.pagination__link') && !e.target.closest('.pagination__link--current')) {
    e.preventDefault();
    const link = e.target.closest('.pagination__link');
    const url = new URL(link.href);
    const params = url.searchParams.toString();
    
    const filters = new CollectionFilters();
    filters.showLoading();
    filters.loadCollection(params, true);
  }
});